import React, { useEffect, useState, useRef } from 'react';
import { useForm } from '../context/FormContext';
import axios from 'axios';
import { totalHours } from '../utils/dotHours';
import { elapsedSeconds } from '../utils/botCheck';

export default function StepSubmission({ onBack }) {
  const { state, dispatch, formStartedAt } = useForm();
  const [status, setStatus] = useState('submitting'); // submitting | success | error
  const [errMsg, setErrMsg] = useState('');
  const submitted = useRef(false);

  const buildPayload = () => ({
    submission_timestamp: new Date().toISOString(),
    company_of_application: state.companyOfApplication,
    personal: {
      first_name: state.personal.firstName,
      middle_name: state.personal.middleName,
      last_name: state.personal.lastName,
      suffix: state.personal.suffix,
      date_of_birth: state.dateOfBirth,
      ssn: state.ssn,
    },
    address: {
      current: {
        street: state.currentAddress.street,
        city: state.currentAddress.city,
        state: state.currentAddress.state,
        zip: state.currentAddress.zip,
        years: parseFloat(state.currentAddress.years),
      },
      previous: parseFloat(state.currentAddress.years) < 3 ? {
        street: state.previousAddress.street,
        city: state.previousAddress.city,
        state: state.previousAddress.state,
        zip: state.previousAddress.zip,
      } : null,
    },
    contact: {
      home_phone: state.contact.homePhone,
      cell_phone: state.contact.cellPhone,
      email: state.contact.email,
    },
    drivers_license: {
      license_number: state.license.number,
      state: state.license.state,
      issue_date: state.license.issueDate,
      class: state.license.class,
      expiration_date: state.license.expirationDate,
      years_held: parseFloat(state.license.yearsHeld),
      endorsements: state.license.endorsements,
      restrictions: state.license.restrictions,
      states_operated: state.license.statesOperated,
      lower_48: !!state.license.lower48,
    },
    yes_no_questions: {
      surety_fidelity_bond: { answer: state.yesNo.surety_fidelity_bond.answer },
      felonies: { answer: state.yesNo.felonies.answer, explanation: state.yesNo.felonies.explanation },
      currently_working: { answer: state.yesNo.currently_working.answer },
      two_jobs: { answer: state.yesNo.two_jobs.answer },
      failed_drug_test: { answer: state.yesNo.failed_drug_test.answer, explanation: state.yesNo.failed_drug_test.explanation },
      refused_drug_test: { answer: state.yesNo.refused_drug_test.answer, explanation: state.yesNo.refused_drug_test.explanation },
      license_other_states: { answer: state.yesNo.license_other_states.answer, previous_state: state.yesNo.license_other_states.previousState, previous_license_number: state.yesNo.license_other_states.previousLicenseNumber },
      license_suspended_revoked: { answer: state.yesNo.license_suspended_revoked.answer, explanation: state.yesNo.license_suspended_revoked.explanation },
      denied_license: { answer: state.yesNo.denied_license.answer, explanation: state.yesNo.denied_license.explanation },
      traffic_convictions: { answer: state.yesNo.traffic_convictions.answer, explanation: state.yesNo.traffic_convictions.explanation },
      accidents_cmv: { answer: state.yesNo.accidents_cmv.answer, explanation: state.yesNo.accidents_cmv.explanation },
      fatality_hazmat: { answer: state.yesNo.fatality_hazmat.answer, explanation: state.yesNo.fatality_hazmat.explanation },
    },
    job_history: state.jobEntries.map(j => ({
      employer_name: j.employer_name,
      street: j.street,
      city: j.city,
      state: j.state,
      zip: j.zip,
      start_date: j.start_date,
      // end_date is stored, auto-populated, and kept in sync by the ripple in
      // Step22 (editing a start date updates the end date of the entry above).
      // Entry 0 is the current job (null = Present).
      // 🔑 School DOES carry an end_date on the wire. It used to be stripped to
      // null here, which made the school-terminator rule unenforceable on the
      // server -- the one date the rule turns on was deleted at the boundary.
      // School still carries no start_date: it is unbounded backward.
      end_date: j.end_date || null,
      position: j.position,
      dot_regulated: j.dot_regulated,
      is_school: !!j.is_school,
      // Non-working period (unemployment, disability, medical, etc.). Unlike
      // school this keeps its dates and is a normal link in the chain; the
      // reason is free text. Consumers must branch on is_non_working and render
      // the reason, NOT the (empty) employer fields.
      is_non_working: !!j.is_non_working,
      reason: j.is_non_working ? (j.reason || '') : '',
    })),
    // Seven-day on-duty hours. ALWAYS sent, both answers. A "no" carries seven
    // explicit zeros rather than an absent block, so the file records that the
    // driver was asked and answered. The total is derived here from the same
    // days array the driver saw — it is never a field he typed.
    dot_hours: {
      answer: state.dotHours.answer,
      anchored_at: state.dotHours.anchoredAt,
      days: (state.dotHours.days || []).map(d => ({
        date: d.date,
        hours: Number(d.hours) || 0,
      })),
      total: totalHours(state.dotHours.days),
    },
    // Bot defense (item 31). `elapsed_seconds` is the difference between two
    // readings of the SAME monotonic clock on this device, so it needs no
    // reconciling against the server clock and cannot be broken by an NTP
    // correction mid-form. The honeypot is whatever ended up in the hidden
    // field — empty for every human.
    bot_check: {
      elapsed_seconds: elapsedSeconds(formStartedAt.current),
      honeypot: state.botCheck.honeypot,
    },
    signature: {
      printed_name: state.signature.printedName,
      signed: true,
      signature_data: state.signature.signatureData,
      signature_timestamp: new Date().toISOString(),
    },
  });

  const submit = async () => {
    if (submitted.current) return;
    submitted.current = true;
    setStatus('submitting');

    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(buildPayload()));

      // License photo
      if (state.license.photoBase64 && state.license.photoName) {
        const byteString = atob(state.license.photoBase64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        const mime = state.license.photoName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
        const blob = new Blob([ab], { type: mime });
        formData.append('license_photo', blob, state.license.photoName);
      }

      // Client timeout MUST exceed the server's. Ordering is: axios (180s) >
      // nginx proxy_read_timeout (120s) > gunicorn --timeout (120s). If the
      // client gives up first, the server keeps running to completion and
      // returns 200 into a dead socket — the submission succeeds while the
      // driver is told it failed. That inversion is what caused the original
      // "timeout of 30000ms exceeded" reports.
      const res = await axios.post('/api/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });

      if (res.data.success) {
        setStatus('success');
        dispatch({ type: 'RESET' });
      } else {
        throw new Error(res.data.error || 'Unknown error');
      }
    } catch (err) {
      // A timeout or a dropped connection is NOT a known failure. The server
      // may well have completed the submission — we simply never heard back.
      // Telling the driver "failed" here is what drove the repeat taps, so
      // this case gets its own honest status and no retry button.
      const isIndeterminate =
        err.code === 'ECONNABORTED' ||
        err.code === 'ETIMEDOUT' ||
        err.message?.toLowerCase().includes('timeout') ||
        (!err.response && err.request);

      if (isIndeterminate) {
        setStatus('unknown');
        return;
      }

      submitted.current = false;
      setErrMsg(err.response?.data?.error || err.message || 'Submission failed');
      setStatus('error');
    }
  };

  useEffect(() => { submit(); }, []);

  if (status === 'submitting') return (
    <div className="step-card status-screen">
      <div className="spinner" />
      <div className="status-title">Submitting...</div>
      <p className="status-msg">Please wait while your application is being sent. Do not close this page.</p>
    </div>
  );

  if (status === 'success') return (
    <div className="step-card status-screen">
      <div className="status-icon">✅</div>
      <div className="status-title" style={{ color: 'var(--success)' }}>Application Submitted</div>
      <p className="status-msg">Thank you. Your information has been received by Motor Carrier Solutions.<br /><br />You may now close this page.</p>
    </div>
  );

  if (status === 'unknown') return (
    <div className="step-card status-screen">
      <div className="status-icon">⏳</div>
      <div className="status-title">Still Processing</div>
      <p className="status-msg">
        Your connection dropped before we got a confirmation, but your application
        may have gone through.
        <br /><br />
        <strong>Please do not resubmit</strong> — that can file your application twice.
        Call MCS at (606) 474-8854 and we'll confirm whether we received it.
      </p>
    </div>
  );

  return (
    <div className="step-card status-screen">
      <div className="status-icon">❌</div>
      <div className="status-title" style={{ color: 'var(--error)' }}>Submission Failed</div>
      <p className="status-msg">{errMsg}<br /><br />Please try again or contact MCS at (606) 474-8854.</p>
      <button className="btn-next" style={{ marginTop: 20 }} onClick={() => { submitted.current = false; submit(); }}>Try Again</button>
      <button className="btn-back" style={{ marginTop: 8, width: '100%' }} onClick={() => { submitted.current = false; setStatus(null); onBack(); }}>← Back to Review</button>
    </div>
  );
}
